import * as THREE from "three";

/**
 * How the camera frames Captain Bram in a slot (split from bramStage.ts): pure maths over the model's proportions,
 * no renderer.
 */

/** The bubble's (and the tip's) head-and-shoulders bust, or the welcome card's waist-up figure. */
export type BramFraming = "bust" | "hero";
/**
 * The frame runs from a little above the top of his head (model units: he is 1 tall, the plume's tip at 1) down to
 * `belowNeck` under the neck (the bust: shoulders; the welcome figure: the waist), and is at least wide enough for
 * the head with `sideRoom` either side. `orbitDeg` swings the camera round to his right so he stands three-quarters
 * on, turned toward the bubble's words (the old renders' angle); the welcome figure faces the player.
 */
const FRAMING: Readonly<Record<BramFraming, { belowNeck: number; headroom: number; sideRoom: number; orbitDeg: number }>> = {
  // the bubble bust crops the plume (it runs ~0.2 above the helmet) so his face fills the ~100 px frame
  bust: { belowNeck: 0.05, headroom: -0.13, sideRoom: 0, orbitDeg: 18 },
  hero: { belowNeck: 0.34, headroom: 0.04, sideRoom: 0.2, orbitDeg: 8 },
};
export const FOV_DEG = 22;
/** The camera looks down on him a touch, as the old renders did. */
const CAMERA_RISE = 0.08;
/** The top of his head in the normalised frame. */
export const MODEL_TOP = 1;
/** The contract model's neck height and head width (normalised) until a model reports its own. */
export const DEFAULT_NECK_Y = 0.67;
export const DEFAULT_HEAD_WIDTH = 0.42;

/** Aims `camera` so a `w` × `h` slot shows him as `framing` asks. */
export function frameCamera(camera: THREE.PerspectiveCamera, framing: BramFraming, w: number, h: number, neckY: number, headWidth: number): void {
  const f = FRAMING[framing];
  const aspect = Math.max(1, w) / Math.max(1, h);
  const top = MODEL_TOP + f.headroom;
  const span = Math.max(top - (neckY - f.belowNeck), (headWidth + 2 * f.sideRoom) / aspect);
  const cy = top - span / 2;
  const dist = span / 2 / Math.tan(THREE.MathUtils.degToRad(FOV_DEG) / 2);
  const orbit = THREE.MathUtils.degToRad(f.orbitDeg);
  camera.aspect = aspect;
  camera.position.set(-Math.sin(orbit) * dist, cy + CAMERA_RISE, Math.cos(orbit) * dist);
  camera.lookAt(0, cy, 0);
  camera.updateProjectionMatrix();
}
